/* eslint-disable no-multi-assign */
import * as types from 'constants/action_types'
import * as pages from 'constants/pages'
import * as onboardingTypes from 'subapps/onboarding/types'
import storage from 'util/storage'
import { areArraysEqual, groupByToMap, sortByKey, last } from 'util/arrays'
import { capitalize } from 'util/strings'
import { getShortAgentName, getAgentUsername, fullName } from '../util/agents'

const storedCurrentUser = storage.get('currentUser') || {}
const defaultCurrentTicket = {
  addingNote: [],
  id: undefined,
  replyingToTicket: [],
  typingOnTicket: [],
  viewingTicket: [],
}
const defaultState = {
  byId: {},
  viewTicketStatusesById: {},
  replyToTicketStatusesById: {},
  typeOnTicketStatusesById: {},
  addNoteStatusesById: {},
  currentUserId: storedCurrentUser.id,
  currentTicketId: undefined,
  currentTicket: defaultCurrentTicket,
}
const reducers = {}

export function getAvatarUrl(agent) {
  return agent && agent.avatar_url && !agent.avatar_url.match('missing.png')
    ? agent.avatar_url
    : null
}

function build(agents, agent, currentUserId) {
  let agentFullName
  let label = null
  const shortName = getShortAgentName(agents, agent)

  if (agent) {
    agentFullName = fullName(agent)
    if (agent.id === currentUserId) {
      label = 'You'
    } else {
      label = capitalize(shortName)
    }
  }

  const lastName = agent.last_name || ''
  const letter = lastName.charAt(0).toUpperCase()

  return {
    ...agent,
    agentId: agent ? agent.id : undefined,
    label,
    avatarUrl: getAvatarUrl(agent),
    labelFull: agentFullName,
    fullName: agentFullName,
    isAssignedToSelf: agent && agent.id === currentUserId,
    type: 'Agent',
    initial: [agent.first_name, letter].join(' '),
    initials: [agent.first_name, agent.last_name]
      .map(part => part && part.charAt(0))
      .join(''),
    name: [agent.first_name, agent.last_name].join(' '),
    username: agent.username ? getAgentUsername(agent) : null,
    shortName,
  }
}

// eslint-disable-next-line no-multi-assign
reducers[types.UPDATE_APP_DATA] = reducers[types.FETCH_AGENTS_SUCCESS] = (
  state,
  action
) => {
  const { agents = [], currentUser = {} } = action.data
  const newById = {}
  agents.forEach(agent => {
    newById[agent.id] = build(agents, agent, currentUser.id)
  })
  return {
    ...state,
    byId: newById,
    currentUserId: currentUser.id,
  }
}

reducers[types.UPDATE_AGENT] = (state, action) => {
  const currentUserId = state.currentUserId
  const agent = action.data
  const byId = state.byId || {}
  const existingAgent = byId[agent.id]
  const newAgent = build(
    Object.values(byId),
    Object.assign({}, existingAgent || {}, agent),
    currentUserId
  )
  const newById = Object.assign({}, byId, { [agent.id]: newAgent })

  return Object.assign({}, state, {
    byId: newById,
  })
}

reducers[types.REMOVE_AGENT] = (state, action) => {
  const id = action.data
  const byId = state.byId || {}
  const newById = Object.assign({}, byId)
  delete newById[id]
  return Object.assign({}, state, {
    byId: newById,
  })
}

const createAgentCollisionStatusStoreKey = agentAction => {
  return `${agentAction}StatusesById`
}

reducers[types.FETCH_AGENT_COLLISION_STATUSES_SUCCESS] = (state, action) => {
  /** pnagy
   * Sanitize actions: Because we have the agent adding a note chain (adding note > viewing)
   * type which is handled out of the (typing > replying > viewing) change we need to make sure
   * that we have only one of those 2 chains "active" at the same time so if we receive
   * a state which has multiple entries we process only the recent ones
   */

  // We're creating a flatlist of action and
  // inject the type and the agentId into the object
  const actionList = Object.keys(action.data).reduce((result, type) => {
    if (type !== 'viewTicket') {
      const actionsByAgent = action.data[type]
      const item = Object.keys(actionsByAgent).map(agentId => {
        return {
          ...actionsByAgent[agentId],
          agentId,
          type,
        }
      })
      return [...result, ...item]
    }
    return result
  }, [])

  // We're grouping the list
  const actionsByTicketIdAgentId = groupByToMap(
    actionList,
    i => `${i.ticketId}-${i.agentId}`
  )

  // We reduce the list of each key using the most recent action
  const filteredDuplicateData = Array.from(
    actionsByTicketIdAgentId.entries()
  ).reduce(
    // eslint-disable-next-line no-unused-vars
    (result, [key, value]) => {
      const { type, agentId, ...rest } = last(sortByKey(value, 'timestamp'))
      return {
        ...result,
        [type]: {
          ...result[type],
          [agentId]: rest,
        },
      }
    },
    {}
  )

  // We're done, adding the 'viewing ticket' actions back to the data
  const cleanedActions = {
    ...filteredDuplicateData,
    viewTicket: action.data.viewTicket,
  }

  return withCurrentTicket({
    ...state,
    addNoteStatusesById: {
      ...cleanedActions.addNote,
    },
    replyToTicketStatusesById: {
      ...cleanedActions.typeOnTicket,
      ...cleanedActions.replyToTicket,
    },
    typeOnTicketStatusesById: {
      ...cleanedActions.typeOnTicket,
    },
    viewTicketStatusesById: {
      ...cleanedActions.addNote,
      ...cleanedActions.typeOnTicket,
      ...cleanedActions.replyToTicket,
      ...cleanedActions.viewTicket,
    },
  })
}

// Normalises agent statuses for just the current ticket.
// This makes selecting on this data for the current ticket trivial.
function withCurrentTicket(state) {
  if (!state) return state

  const { currentTicketId, currentUserId, currentTicket = {} } = state
  const isOnCurrentTicket = agent => agent.ticketId === currentTicketId
  const findCurrentIds = byId =>
    Object.keys(byId)
      .filter(id => isOnCurrentTicket(byId[id]))
      .filter(id => id !== currentUserId)
      .map(Number)
  const typingIds = findCurrentIds(state.typeOnTicketStatusesById)
  const addingNoteIds = findCurrentIds(state.addNoteStatusesById)
  const replyingIds = findCurrentIds(state.replyToTicketStatusesById)
  const viewingIds = findCurrentIds(state.viewTicketStatusesById)
  const unchanged =
    areArraysEqual(currentTicket.typingOnTicket, typingIds) &&
    areArraysEqual(currentTicket.addingNote, addingNoteIds) &&
    areArraysEqual(currentTicket.replyingToTicket, replyingIds) &&
    areArraysEqual(currentTicket.viewingTicket, viewingIds)

  if (unchanged) {
    if (state.currentTicket) return state

    // Ensure its initialized properly
    return {
      ...state,
      currentTicket: {
        ...defaultCurrentTicket,
        id: currentTicketId,
      },
    }
  }

  return {
    ...state,
    currentTicket: {
      addingNote: addingNoteIds,
      id: currentTicketId,
      replyingToTicket: replyingIds,
      typingOnTicket: typingIds,
      viewingTicket: viewingIds,
    },
  }
}

reducers[types.UPDATE_AGENT_TICKET_COLLISION_STATUS] = (state, action) => {
  const { agentAction, agentId, ticketId, timestamp } = action.data
  const changedStatuses = {}
  const addNoteKey = createAgentCollisionStatusStoreKey('addNote')
  const typeOnTicketKey = createAgentCollisionStatusStoreKey('typeOnTicket')
  const replyToTicketKey = createAgentCollisionStatusStoreKey('replyToTicket')
  const viewTicketKey = createAgentCollisionStatusStoreKey('viewTicket')
  // ===================================================
  //         Logic table for agent collisions
  // ---------------------------------------------------
  //                         view    reply    type    leave note
  // start adding a note      x                          x
  //  stop adding a note      x
  //         start typing     x        x       x
  //          stop typing     x        x
  //       start replying     x        x
  //        stop replying     x
  //        start viewing     x
  //         stop viewing
  // ===================================================
  switch (agentAction) {
    case 'addNote':
      changedStatuses[typeOnTicketKey] = {
        ...state[typeOnTicketKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[replyToTicketKey] = {
        ...state[replyToTicketKey],
        [agentId]: { ticketId: null, timestamp },
      }
      if (ticketId) {
        changedStatuses[addNoteKey] = {
          ...state[addNoteKey],
          [agentId]: { ticketId, timestamp },
        }
        changedStatuses[viewTicketKey] = {
          ...state[viewTicketKey],
          [agentId]: { ticketId, timestamp },
        }
      }
      break
    case 'typeOnTicket':
      changedStatuses[typeOnTicketKey] = {
        ...state[typeOnTicketKey],
        [agentId]: { ticketId, timestamp },
      }
      changedStatuses[addNoteKey] = {
        ...state[addNoteKey],
        [agentId]: { ticketId: null, timestamp },
      }
      if (ticketId) {
        changedStatuses[replyToTicketKey] = {
          ...state[replyToTicketKey],
          [agentId]: { ticketId, timestamp },
        }
        changedStatuses[viewTicketKey] = {
          ...state[viewTicketKey],
          [agentId]: { ticketId, timestamp },
        }
      }
      break
    case 'replyToTicket':
      changedStatuses[typeOnTicketKey] = {
        ...state[typeOnTicketKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[addNoteKey] = {
        ...state[addNoteKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[replyToTicketKey] = {
        ...state[replyToTicketKey],
        [agentId]: { ticketId, timestamp },
      }
      if (ticketId) {
        changedStatuses[viewTicketKey] = {
          ...state[viewTicketKey],
          [agentId]: { ticketId, timestamp },
        }
      }
      break
    case 'viewTicket':
      changedStatuses[typeOnTicketKey] = {
        ...state[typeOnTicketKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[replyToTicketKey] = {
        ...state[replyToTicketKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[addNoteKey] = {
        ...state[addNoteKey],
        [agentId]: { ticketId: null, timestamp },
      }
      changedStatuses[viewTicketKey] = {
        ...state[viewTicketKey],
        [agentId]: { ticketId, timestamp },
      }
      break
    default:
      return state
  }

  return withCurrentTicket({
    ...state,
    ...changedStatuses,
  })
}

reducers[pages.TICKET_PAGE] = reducers[pages.TICKET_REPLY_PAGE] = reducers[
  pages.TICKET_REPLY_CHANGESET_PAGE
] = reducers[pages.TICKET_COMMENT_PAGE] = reducers[
  pages.TICKET_FORWARD_PAGE
] = reducers[pages.TICKET_FORWARD_CHANGESET_PAGE] = (
  state,
  { payload: { id } }
) => {
  if (!id) return state

  if (state.currentTicketId === id) return state

  return withCurrentTicket({
    ...state,
    currentTicketId: id,
  })
}

reducers[pages.SEARCH_PAGE] = (
  state,
  { meta: { query: { conversationId } = {} } = {} }
) => {
  if (!conversationId) return state

  if (state.currentTicketId === conversationId) return state

  return withCurrentTicket({
    ...state,
    currentTicketId: conversationId,
  })
}

reducers[onboardingTypes.SIGNUP_SUCCESS] = (
  state,
  { payload: { user } = {} }
) => {
  if (!user) return state
  if (!user.id) return state
  return {
    ...state,
    currentUserId: user.id,
  }
}

export default function reducer(state = defaultState, action) {
  // this is here because a long reducer with many ifs is unreadable
  const handler = reducers[action.type]
  if (handler) return handler(state, action)
  return state
}
